import { createFileRoute, Link } from '@tanstack/react-router'
import { BRAND_FALLBACK } from '@/components/marketing/config'
import { Brand, LegalPage, SupportEmail } from '@/components/marketing/LegalPage'

export const Route = createFileRoute('/privacy')({
  head: () => ({
    meta: [
      { title: `Privacy policy · ${BRAND_FALLBACK}` },
      {
        name: 'description',
        content: 'What we collect, what AI models see, how long we keep data, and how to export or delete it.',
      },
    ],
  }),
  component: Privacy,
})

function Privacy() {
  return (
    <LegalPage
      title="Privacy policy"
      intro={
        <p>
          This policy explains what <Brand /> collects when you use our website and service, what we do with it, who
          processes it for us, and how long we keep it. The short version: AI models only ever see your public website
          content, and you can export or delete your data at any time.
        </p>
      }
    >
      <h2 id="collect">1. What we collect</h2>
      <h3>When you request an audit</h3>
      <ul>
        <li>your business name and website address</li>
        <li>the public content of that website: pages, images, files, styles and screenshots of public pages</li>
        <li>your IP address, to prevent abuse and enforce rate limits</li>
        <li>where you came from, such as the referring site or campaign tags in the link you followed</li>
      </ul>
      <h3>When you request a preview or join the waitlist</h3>
      <ul>
        <li>your email address</li>
        <li>your acceptance of the terms, your marketing choice and your processing choice (see section 3)</li>
      </ul>
      <h3>When you become a customer</h3>
      <ul>
        <li>billing details, which Stripe collects and holds. We never see or store your full card number.</li>
        <li>invoices, your order history, your approval and the notes you send about your site</li>
        <li>the email address your contact form sends to</li>
        <li>messages you send to support</li>
      </ul>
      <h3>From visitors to your rebuilt site</h3>
      <p>
        Messages sent through your site’s contact form are emailed to you and shown in your account. We process them
        on your behalf and use them for nothing else.
      </p>

      <h2 id="use">2. How we use it</h2>
      <ul>
        <li>to run the audit, build your preview and rebuild your site</li>
        <li>to take payment, send receipts and keep accounting records</li>
        <li>to email you about your order: previews, reminders, go-live and support replies</li>
        <li>to protect the service from abuse</li>
        <li>to send product news, only if you opted in. Every such email has an unsubscribe link.</li>
      </ul>
      <p>We don’t sell personal data.</p>

      <h2 id="ai">3. What AI models see</h2>
      <p>
        AI models rebuild and review sites. They only receive your public site content: the same pages, images and
        styles anyone can see by visiting your site. Your name, email address, billing details and your visitors’ form
        submissions are never sent to a model provider.
      </p>
      <p>
        By default, some building steps use DeepSeek models through OpenRouter, and reviews use Anthropic’s Claude
        models. When you request a preview you can choose Claude-only processing, which keeps your content away from
        DeepSeek. You can also ask for it later at <SupportEmail />.
      </p>

      <h2 id="cookies">4. Cookies</h2>
      <p>We set one cookie of our own:</p>
      <ul>
        <li>
          a session cookie that keeps you signed in to your order. It can’t be read by scripts and is only sent to our
          own site.
        </li>
      </ul>
      <p>
        Our audit form may use Cloudflare Turnstile to check that a person, not a bot, is submitting it. Turnstile
        processes technical information about your browser for that check only.
      </p>

      <h2 id="retention">5. How long we keep data</h2>
      <table>
        <thead>
          <tr>
            <th scope="col">Data</th>
            <th scope="col">Kept for</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Audits</td>
            <td>30 days</td>
          </tr>
          <tr>
            <td>Email addresses and details of people who didn’t place an order (leads)</td>
            <td>Deleted 30 days after the last activity</td>
          </tr>
          <tr>
            <td>Previews that expire unpaid</td>
            <td>Deleted 30 days after expiry</td>
          </tr>
          <tr>
            <td>Contact form submissions from your site’s visitors</td>
            <td>90 days</td>
          </tr>
          <tr>
            <td>Copies of your old site, screenshots and review reports made during the rebuild</td>
            <td>90 days after your site goes live</td>
          </tr>
          <tr>
            <td>Your site’s content after a final failed payment</td>
            <td>90 days</td>
          </tr>
          <tr>
            <td>Your site after you cancel</td>
            <td>Online for 30 days, then removed</td>
          </tr>
          <tr>
            <td>Invoices and payment records</td>
            <td>As long as tax law requires</td>
          </tr>
        </tbody>
      </table>
      <p>
        Site databases are also backed up: a restore point for the last 30 days, and a weekly backup copy. Backups age
        out on the same schedule.
      </p>

      <h2 id="rights">6. Export, deletion and your other rights</h2>
      <p>
        You can ask us at any time to export your data or your site’s content, or to delete them. You can also ask what
        we hold about you, ask us to correct it, or object to how we use it. Email <SupportEmail /> from the address you
        used with us and we will act on it. We keep only what the law requires us to keep, such as invoices.
      </p>
      <p>
        If you are unhappy with how we handle your data, you can also complain to your local data protection authority.
      </p>

      <h2 id="processors">7. Who processes data for us</h2>
      <p>
        We use a small number of providers for hosting, payments, AI models, backups and email. They are listed, with
        what each one handles, on our <Link to="/subprocessors">subprocessors page</Link>. Some are based outside your
        country, including in the United States and China.
      </p>

      <h2 id="security">8. Security</h2>
      <p>
        Customer sites run in isolation from each other. The systems that rebuild sites hold no payment or email
        credentials. Access to customer data is limited to the people who run the service.
      </p>

      <h2 id="changes">9. Changes to this policy</h2>
      <p>
        When we change this policy in a way that affects you, we update the date at the top and email active
        customers.
      </p>

      <h2 id="contact">10. Contact</h2>
      <p>
        Privacy questions and requests: <SupportEmail />.
      </p>
    </LegalPage>
  )
}
